import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Award, CheckCircle, Users, Target, TrendingUp, ArrowRight, MessageCircle, Shield, Clock } from 'lucide-react';
import { backgroundImages, companyInfo } from '../data/mock';

const AboutPage = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-construction-darkblue text-white py-20 overflow-hidden">
        <div className="absolute inset-0 opacity-15">
          <img 
            src={backgroundImages.team} 
            alt="Construction team" 
            className="w-full h-full object-cover"
          />
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-block bg-construction-yellow text-construction-darkblue px-4 py-2 rounded-full text-sm font-bold mb-6">
              🏗️ Established 2021
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Supporting the UK Construction Workforce Since 2021
            </h1>
            <p className="text-xl text-gray-200 leading-relaxed mb-8">
              Helping construction workers gain the right qualifications, cards, and certifications quickly and efficiently.
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center justify-center gap-2 bg-construction-yellow text-construction-darkblue px-8 py-4 rounded-lg font-bold text-lg hover:bg-yellow-500 transition-all hover:scale-105 shadow-lg"
            >
              Contact Our Team Today
              <ArrowRight size={20} />
            </Link>
          </div>
        </div>
      </section>

      {/* Who We Are */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-construction-darkblue mb-4">
                Who We Are
              </h2>
              <div className="w-20 h-1 bg-construction-yellow mx-auto mb-8"></div>
            </div>

            <div className="bg-gray-50 rounded-xl p-8 mb-8">
              <div className="prose prose-lg max-w-none">
                <p className="text-gray-700 leading-relaxed mb-6 text-lg">
                  <strong className="text-construction-darkblue">Construction Cards Helpline Limited</strong> was established on <strong>14th October 2021</strong> as a UK registered company dedicated to supporting construction workers across the United Kingdom.
                </p>
                
                <div className="bg-construction-yellow/10 border-l-4 border-construction-yellow p-6 rounded-r-lg mb-6">
                  <p className="text-construction-darkblue font-semibold mb-2">
                    🏢 Official Company Information:
                  </p>
                  <ul className="space-y-2 text-gray-700">
                    <li><strong>Company Name:</strong> Construction Cards Helpline Limited</li>
                    <li><strong>Company Number:</strong> 13678504</li>
                    <li><strong>Established:</strong> 14th October 2021</li>
                    <li><strong>Location:</strong> United Kingdom</li>
                  </ul>
                </div>

                <p className="text-gray-700 leading-relaxed mb-4 text-lg">
                  We are focused on making the process of obtaining <strong>CSCS, NVQ, CPCS, and NPORS qualifications</strong> simple and stress-free for construction professionals at all levels.
                </p>

                <p className="text-gray-700 leading-relaxed text-lg">
                  Our team understands the challenges construction workers face in navigating certification requirements. We're here to provide clear guidance, expert support, and efficient processing to help you get back on site quickly.
                </p>
              </div>
            </div>

            <div className="grid md:grid-cols-3 gap-6 mt-8">
              <div className="bg-white border-2 border-construction-yellow rounded-xl p-6 text-center">
                <div className="bg-construction-yellow/10 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Shield className="text-construction-yellow" size={32} />
                </div>
                <h3 className="font-bold text-construction-darkblue text-xl mb-2">UK Registered</h3>
                <p className="text-gray-600">Company No. 13678504</p>
              </div>

              <div className="bg-white border-2 border-construction-yellow rounded-xl p-6 text-center">
                <div className="bg-construction-yellow/10 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Clock className="text-construction-yellow" size={32} />
                </div>
                <h3 className="font-bold text-construction-darkblue text-xl mb-2">Since 2021</h3>
                <p className="text-gray-600">Established October 2021</p>
              </div>

              <div className="bg-white border-2 border-construction-yellow rounded-xl p-6 text-center">
                <div className="bg-construction-yellow/10 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Users className="text-construction-yellow" size={32} />
                </div>
                <h3 className="font-bold text-construction-darkblue text-xl mb-2">UK-Wide</h3>
                <p className="text-gray-600">Nationwide Support</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What We Do */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-construction-darkblue mb-4">
                What We Do
              </h2>
              <div className="w-20 h-1 bg-construction-yellow mx-auto mb-6"></div>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                Comprehensive construction certification and training services across the UK
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {[
                {
                  title: "CSCS Card Applications",
                  description: "Fast processing for all CSCS card types - Green, Blue, Gold, and Black cards",
                  icon: Award
                },
                {
                  title: "CSCS Test Booking",
                  description: "Quick and convenient test booking at centres nationwide",
                  icon: CheckCircle
                },
                {
                  title: "NVQ Level 2-7 Qualifications",
                  description: "Work-based qualifications for all CSCS card types across all construction trades",
                  icon: Target
                },
                {
                  title: "CPCS Plant Operator Courses",
                  description: "Comprehensive training for plant machinery operators including excavators, dumpers, and more",
                  icon: TrendingUp
                },
                {
                  title: "NPORS Training Courses",
                  description: "National Plant Operators Registration Scheme training and certification",
                  icon: Shield
                },
                {
                  title: "Construction Certification Guidance",
                  description: "Expert advice on which qualifications you need for your specific role",
                  icon: Users
                }
              ].map((service, index) => {
                const Icon = service.icon;
                return (
                  <div key={index} className="bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow">
                    <div className="flex items-start gap-4">
                      <div className="bg-construction-yellow/10 p-3 rounded-lg flex-shrink-0">
                        <Icon className="text-construction-yellow" size={28} />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-construction-darkblue mb-2">
                          {service.title}
                        </h3>
                        <p className="text-gray-700 leading-relaxed">
                          {service.description}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="mt-12 bg-construction-darkblue text-white rounded-xl p-8">
              <h3 className="text-2xl font-bold mb-4 text-center">Our Service Highlights</h3>
              <div className="grid md:grid-cols-4 gap-6">
                <div className="text-center">
                  <CheckCircle className="text-construction-yellow mx-auto mb-2" size={32} />
                  <p className="font-semibold">Fast Processing</p>
                </div>
                <div className="text-center">
                  <CheckCircle className="text-construction-yellow mx-auto mb-2" size={32} />
                  <p className="font-semibold">Expert Support</p>
                </div>
                <div className="text-center">
                  <CheckCircle className="text-construction-yellow mx-auto mb-2" size={32} />
                  <p className="font-semibold">Nationwide Assistance</p>
                </div>
                <div className="text-center">
                  <CheckCircle className="text-construction-yellow mx-auto mb-2" size={32} />
                  <p className="font-semibold">Step-by-Step Guidance</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Mission */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-8">
              <h2 className="text-3xl md:text-4xl font-bold text-construction-darkblue mb-4">
                Our Mission
              </h2>
              <div className="w-20 h-1 bg-construction-yellow mx-auto"></div>
            </div>

            <div className="bg-construction-yellow/5 border-2 border-construction-yellow rounded-xl p-8 md:p-12">
              <p className="text-xl md:text-2xl text-gray-800 leading-relaxed text-center mb-6 font-medium">
                "To empower construction workers across the UK by providing accessible, efficient, and reliable certification services that help them secure employment, ensure site compliance, and progress in their careers."
              </p>
              
              <div className="grid md:grid-cols-2 gap-6 mt-8">
                <div className="flex items-start gap-3">
                  <CheckCircle className="text-construction-yellow flex-shrink-0 mt-1" size={24} />
                  <div>
                    <h4 className="font-bold text-construction-darkblue mb-1">Secure Employment</h4>
                    <p className="text-gray-700">Helping workers obtain the cards they need to access construction sites</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="text-construction-yellow flex-shrink-0 mt-1" size={24} />
                  <div>
                    <h4 className="font-bold text-construction-darkblue mb-1">Site Compliance</h4>
                    <p className="text-gray-700">Ensuring all certifications meet UK construction industry standards</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="text-construction-yellow flex-shrink-0 mt-1" size={24} />
                  <div>
                    <h4 className="font-bold text-construction-darkblue mb-1">Accessible Qualifications</h4>
                    <p className="text-gray-700">Making the certification process straightforward and stress-free</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="text-construction-yellow flex-shrink-0 mt-1" size={24} />
                  <div>
                    <h4 className="font-bold text-construction-darkblue mb-1">Career Progression</h4>
                    <p className="text-gray-700">Supporting advancement from labourer to management levels</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-construction-darkblue mb-4">
                Why Choose Us
              </h2>
              <div className="w-20 h-1 bg-construction-yellow mx-auto"></div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {[
                {
                  title: "Professional & Experienced Team",
                  description: "Our team has extensive knowledge of UK construction certification requirements and processes"
                },
                {
                  title: "Clear Guidance from Start to Finish",
                  description: "We guide you through every step, from choosing the right card to receiving your certification"
                },
                {
                  title: "Reliable Customer Support",
                  description: "Friendly, responsive team available to answer your questions and provide assistance"
                },
                {
                  title: "Transparent Process",
                  description: "No hidden fees or surprises - clear information about timelines, requirements, and costs"
                },
                {
                  title: "Dedicated to UK Construction Industry",
                  description: "Specialist focus on construction certification means we understand your specific needs"
                },
                {
                  title: "Fast & Efficient Service",
                  description: "We process applications quickly to get you back on site with minimal delay"
                }
              ].map((item, index) => (
                <div key={index} className="bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow border-l-4 border-construction-yellow">
                  <h3 className="text-xl font-bold text-construction-darkblue mb-3">
                    ✓ {item.title}
                  </h3>
                  <p className="text-gray-700 leading-relaxed">
                    {item.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-construction-darkblue text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Ready to Get Qualified?
          </h2>
          <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
            Don't delay your certification. Contact our team today and let us help you get the cards you need to work on UK construction sites.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="inline-flex items-center justify-center gap-2 bg-construction-yellow text-construction-darkblue px-8 py-4 rounded-lg font-bold text-lg hover:bg-yellow-500 transition-all hover:scale-105 shadow-lg"
            >
              Apply Now
              <ArrowRight size={20} />
            </Link>
            <a
              href={`tel:${companyInfo.phone}`}
              className="inline-flex items-center justify-center gap-2 bg-white text-construction-darkblue px-8 py-4 rounded-lg font-bold text-lg hover:bg-gray-100 transition-all hover:scale-105 shadow-lg"
            >
              <Phone size={24} />
              Speak to an Advisor
            </a>
            <a
              href={`https://wa.me/${companyInfo.whatsapp.replace(/\+/g, '')}?text=Hi, I'd like to speak with an advisor`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 bg-green-600 text-white px-8 py-4 rounded-lg font-bold text-lg hover:bg-green-700 transition-all hover:scale-105 shadow-lg"
            >
              <MessageCircle size={24} />
              WhatsApp
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
